<script setup lang="ts">
import LocationMap from '~/shared-kernel/presentation/LocationMap.client.vue'
import { eventLocationDirectionsUrl } from '~/modules/public/purchase/application/event-location-directions'
import { isValidLocationCoordinates } from '~/shared-kernel/domain/location'
import PublicEventMediaPlayer from '~/modules/public/purchase/presentation/PublicEventMediaPlayer.vue'
import PublicPicture from '~/modules/public/purchase/presentation/media/PublicPicture.vue'
import type { PublicEvent } from '~/modules/public/purchase/domain/public-purchase.types'
import type { PublicEventInformation } from '~/modules/public/purchase/domain/public-event-information'
import { getPublicEventInformation } from '~/modules/public/purchase/infrastructure/public-event-information.repository'
import { formatBusinessDate } from '~/shared-kernel/domain/business-time'

const props = defineProps<{
  event: PublicEvent
}>()
const emit = defineEmits<{
  organizers: [names: string[]]
  pulep: [code: string | null]
  audio: [src: string | null]
  tour: []
}>()
const { t, locale } = useI18n()
const open = ref(false)
const guideOpen = ref(false)
const videoZoom = ref(100)
function resizeVideo(delta: number) {
  videoZoom.value = Math.min(150, Math.max(75, videoZoom.value + delta))
}
const information = shallowRef<PublicEventInformation | null>(null)
const loading = ref(false)
const failed = ref(false)
let generation = 0
async function load() {
  const request = ++generation
  information.value = null
  emit('organizers', [])
  emit('pulep', null)
  emit('audio', null)
  loading.value = true
  failed.value = false
  try {
    const result = await getPublicEventInformation(props.event.slug)
    if (request !== generation) return
    information.value = result
    emit('organizers', result.organizers)
    emit('pulep', result.pulepCode)
    emit(
      'audio',
      result.media.find((asset) => asset.kind === 'promotional_audio')
        ?.contentPath ?? null,
    )
  } catch {
    if (request === generation) failed.value = true
  } finally {
    if (request === generation) loading.value = false
  }
}
// Optional extras: fetching them during SSR would only discard the result.
watch(() => props.event.slug, load, { immediate: import.meta.client })
onBeforeUnmount(() => {
  generation++
})
const key = (name: string) => `public.purchase.eventHelp.${name}`
function date(value?: string | null) {
  if (!value || !Number.isFinite(Date.parse(value)))
    return t(key('unscheduled'))
  return formatBusinessDate(value, locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}
function period(start?: string | null, end?: string | null) {
  if (start && end) return `${date(start)} — ${date(end)}`
  if (start) return t(key('periodFrom'), { date: date(start) })
  if (end) return t(key('periodUntil'), { date: date(end) })
  return null
}
const money = (currency: string, amount: number) =>
  new Intl.NumberFormat(locale.value, { style: 'currency', currency }).format(
    amount,
  )
const count = (value: number) => new Intl.NumberFormat(locale.value).format(value)
// Images that failed to load are removed instead of showing alt text.
const brokenImages = reactive(new Set<string>())
const poster = computed(() => {
  const banner = props.event.media?.verticalBanner
  return banner && !brokenImages.has(banner.contentPath) ? banner : null
})
type PhaseRow = PublicEventInformation['phases'][number]
const phaseGroups = computed(() => {
  const groups = new Map<
    string,
    { name: string; rows: (PhaseRow & { period: string | null })[] }
  >()
  for (const phase of information.value?.phases ?? []) {
    const group = groups.get(phase.phaseId) ?? { name: phase.phase, rows: [] }
    group.rows.push({ ...phase, period: period(phase.startsAt, phase.endsAt) })
    groups.set(phase.phaseId, group)
  }
  return [...groups.entries()].map(([id, { name, rows }]) => {
    const shared = rows.every((row) => row.period === rows[0]!.period)
    return {
      id,
      name,
      rows,
      period: shared ? rows[0]!.period : null,
      rowPeriods: !shared,
    }
  })
})
const promotionalVideo = computed(() =>
  information.value?.media.find((asset) => asset.kind === 'promotional_video'),
)
const gallery = computed(
  () =>
    information.value?.media.filter((asset) =>
      ['gallery_image', 'gallery_video'].includes(asset.kind),
    ) ?? [],
)
const slide = ref(0)
const currentGalleryAsset = computed(() => gallery.value[slide.value])
watch(
  () => props.event.slug,
  () => {
    slide.value = 0
    videoZoom.value = 100
  },
)
const floorPlan = computed(() =>
  information.value?.media.find((asset) => asset.kind === 'floor_plan'),
)
const venueLocation = computed(() =>
  isValidLocationCoordinates(information.value?.location)
    ? information.value.location
    : null,
)
const directionsUrl = computed(() =>
  eventLocationDirectionsUrl(venueLocation.value),
)
const activeSection = ref('overview')
watch(open, (value) => {
  if (value) activeSection.value = 'overview'
})
const tabs = useTemplateRef<{ $el: HTMLElement }>('tabs')
// The tab row scrolls horizontally on phones; keep the active tab visible.
watch(activeSection, async () => {
  await nextTick()
  tabs.value?.$el
    .querySelector('[role="tab"][data-state="active"]')
    ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
})
const sectionItems = computed(() => [
  {
    value: 'overview',
    slot: 'overview',
    label: t(key('overview')),
    icon: 'i-lucide-info',
  },
  ...(information.value
    ? [
        {
          value: 'rates',
          slot: 'rates',
          label: t(key('rates')),
          icon: 'i-lucide-ticket',
        },
      ]
    : []),
  ...(gallery.value.length
    ? [
        {
          value: 'gallery',
          slot: 'gallery',
          label: t(key('gallery')),
          icon: 'i-lucide-images',
        },
      ]
    : []),
  ...(venueLocation.value && directionsUrl.value
    ? [
        {
          value: 'location',
          slot: 'location',
          label: t(key('locationTab')),
          icon: 'i-lucide-map-pin',
        },
      ]
    : []),
])
// Numbered steps read in order; troubleshooting is reference help, so it gets an icon instead of a number.
const guideItems = computed(() => [
  'choose', 'map', 'quantity', 'cart', 'reservation', 'promotion', 'checkout', 'tickets', 'troubleshooting',
].map((section, index) => ({
  value: section,
  step: section === 'troubleshooting' ? null : index + 1,
  label: t(key(`steps.${section}.title`)).replace(/^\d+\.\s*/, ''),
  content: t(key(`steps.${section}.body`)),
})))
</script>

<template>
  <div
    class="purchase-help-actions"
    role="group"
    :aria-label="t(key('details'))"
    data-purchase-tour="event-help"
  >
    <TqButton
      class="purchase-help-actions__details"
      variant="primary"
      icon="i-lucide-info"
      aria-haspopup="dialog"
      @click="open = true"
    >
      {{ t(key('details')) }}
      <UIcon
        class="purchase-help-actions__arrow"
        name="i-lucide-arrow-up-right"
        aria-hidden="true"
      />
    </TqButton>
    <TqButton
      variant="secondary"
      icon="i-lucide-book-open"
      aria-haspopup="dialog"
      @click="guideOpen = true"
      >{{ t(key('guide')) }}</TqButton
    >
    <TqButton variant="secondary" icon="i-lucide-route" @click="emit('tour')">{{
      t(key('tour'))
    }}</TqButton>
  </div>
  <UModal
    v-model:open="open"
    :title="event.name"
    :ui="{
      content:
        'left-0 top-0 h-dvh w-dvw max-h-none max-w-none translate-x-0 translate-y-0 rounded-none shadow-none ring-0 sm:left-1/2 sm:top-1/2 sm:h-[min(52rem,90dvh)] sm:w-[calc(100%-2rem)] sm:max-h-[90dvh] sm:max-w-5xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:shadow-lg sm:ring',
      header: 'pe-14 sm:pe-16',
      wrapper: 'min-w-0',
      title: 'min-w-0 text-lg leading-snug [overflow-wrap:anywhere]',
      close: 'top-3 end-3 sm:top-4 sm:end-4',
      body: 'min-h-0 overflow-y-auto p-0',
    }"
  >
    <template #description>
      <span class="purchase-event-info__meta">
        {{ t(key('details')) }}
        <TqBadge v-if="information?.pulepCode"
          >PULEP {{ information.pulepCode }}</TqBadge
        >
      </span>
    </template>
    <template #body>
      <div class="purchase-event-info">
        <p v-if="loading" role="status">{{ $t('accessibility.loading') }}</p>
        <div v-else-if="failed" role="alert">
          <p>{{ t(key('loadError')) }}</p>
          <TqButton compact @click="load">{{ t(key('retry')) }}</TqButton>
        </div>
        <UTabs
          v-model="activeSection"
          :items="sectionItems"
          variant="link"
          ref="tabs"
          size="md"
          class="purchase-event-info__tabs"
          :ui="{
            list: 'flex-nowrap gap-x-0.5 overflow-x-auto',
            indicator: 'hidden',
            trigger: 'flex-none gap-1.5 px-2.5',
            content: 'min-w-0',
          }"
        >
          <template #overview>
            <div class="purchase-event-info__overview">
              <section v-if="promotionalVideo">
                <h3>{{ t(key('promoVideo')) }}</h3>
                <PublicEventMediaPlayer
                  kind="video"
                  natural-size
                  :auto-play="open"
                  class="purchase-event-info__video"
                  :style="{ '--purchase-video-scale': videoZoom / 100 }"
                  :src="promotionalVideo.contentPath"
                  :label="t(key('promoVideo'))"
                />
                <div
                  class="purchase-event-info__video-sizing"
                  role="group"
                  :aria-label="t(key('videoSize'))"
                >
                  <TqButton
                    compact
                    variant="secondary"
                    icon="i-lucide-zoom-out"
                    :aria-label="t(key('decreaseVideoSize'))"
                    :disabled="videoZoom <= 75"
                    @click="resizeVideo(-25)"
                  >
                    <span class="purchase-event-info__control-label">{{
                      t(key('reduce'))
                    }}</span>
                  </TqButton>
                  <span role="status" aria-live="polite" aria-atomic="true"
                    >{{ videoZoom }}%</span
                  >
                  <TqButton
                    compact
                    variant="secondary"
                    icon="i-lucide-zoom-in"
                    :aria-label="t(key('increaseVideoSize'))"
                    :disabled="videoZoom >= 150"
                    @click="resizeVideo(25)"
                  >
                    <span class="purchase-event-info__control-label">{{
                      t(key('enlarge'))
                    }}</span>
                  </TqButton>
                </div>
              </section>
              <div class="purchase-event-info__about">
              <div class="purchase-event-info__hero">
                <div v-if="poster" class="purchase-event-info__poster">
                  <PublicPicture
                    class="purchase-event-info__banner"
                    :image="poster"
                    sizes="(min-width: 768px) 440px, 90vw"
                    :alt="event.name"
                    @error="brokenImages.add(poster.contentPath)"
                  />
                </div>
                <section>
                  <h3>{{ t(key('eventFacts')) }}</h3>
                  <ul class="purchase-event-info__facts">
                    <li>
                      <span class="purchase-event-info__fact-icon"
                        ><UIcon name="i-lucide-calendar" aria-hidden="true"
                      /></span>
                      <div>
                        <span class="purchase-event-info__fact-label">{{ t(key('eventDate')) }}</span>
                        <strong>{{ date(event.occurrence.startsAt) }}</strong>
                      </div>
                    </li>
                    <li v-if="event.venue">
                      <span class="purchase-event-info__fact-icon"
                        ><UIcon name="i-lucide-map-pin" aria-hidden="true"
                      /></span>
                      <div>
                        <span class="purchase-event-info__fact-label">{{ t(key('venue')) }}</span>
                        <strong>{{ event.venue.name }}</strong>
                        <span
                          v-if="event.venue.address"
                          class="purchase-event-info__address"
                          >{{ event.venue.address }}</span
                        >
                      </div>
                    </li>
                    <li v-if="information?.organizers.length">
                      <span class="purchase-event-info__fact-icon"
                        ><UIcon name="i-lucide-building-2" aria-hidden="true"
                      /></span>
                      <div>
                        <span class="purchase-event-info__fact-label">{{ t(key('organizers')) }}</span>
                        {{ information.organizers.join(' · ') }}
                      </div>
                    </li>
                    <li v-if="event.artists?.length">
                      <span class="purchase-event-info__fact-icon"
                        ><UIcon name="i-lucide-mic" aria-hidden="true"
                      /></span>
                      <div>
                        <span class="purchase-event-info__fact-label">{{ t(key('artists')) }}</span>
                        {{
                          event.artists.map((artist) => artist.name).join(' · ')
                        }}
                      </div>
                    </li>
                    <li v-if="event.genres?.length">
                      <span class="purchase-event-info__fact-icon"
                        ><UIcon name="i-lucide-music" aria-hidden="true"
                      /></span>
                      <div>
                        <span class="purchase-event-info__fact-label">{{ t(key('genres')) }}</span>
                        {{
                          event.genres.map((genre) => genre.name).join(' · ')
                        }}
                      </div>
                    </li>
                    <li>
                      <span class="purchase-event-info__fact-icon"
                        ><UIcon name="i-lucide-tag" aria-hidden="true"
                      /></span>
                      <div>
                        <span class="purchase-event-info__fact-label">{{ t(key('category')) }}</span>
                        {{ event.eventType.name }}
                      </div>
                    </li>
                  </ul>
                </section>
              </div>
              <section v-if="information?.services.length">
                <h3>{{ t(key('services')) }}</h3>
                <ul class="purchase-event-info__services">
                  <li v-for="service in information.services" :key="service">
                    <TqBadge>{{ service }}</TqBadge>
                  </li>
                </ul>
              </section>
              <section v-if="floorPlan && !brokenImages.has(floorPlan.contentPath)">
                <h3>{{ t(key('eventPlan')) }}</h3>
                <img
                  class="purchase-event-info__floor-plan"
                  :src="floorPlan.contentPath"
                  :alt="t(key('eventPlan'))"
                  loading="lazy"
                  @error="brokenImages.add(floorPlan.contentPath)"
                />
              </section>
              </div>
            </div>
          </template>
          <!-- Prices and availability answer one question ("what can I buy?"), so both stay visible together. -->
          <template #rates>
            <div v-if="information" class="purchase-event-info__rates">
              <section>
                <h3>{{ t(key('phases')) }}</h3>
                <p class="purchase-event-info__hint">{{ t(key('phaseHelp')) }}</p>
                <div class="purchase-event-info__groups">
                  <section
                    v-for="group in phaseGroups"
                    :key="group.id"
                    class="purchase-event-info__group"
                  >
                    <header>
                      <h4>{{ group.name }}</h4>
                      <span v-if="group.period">{{ group.period }}</span>
                    </header>
                    <ul role="list" :aria-label="group.name">
                      <li v-for="(phase, index) in group.rows" :key="index">
                        <div class="purchase-event-info__name">
                          <span>{{ phase.locality }}</span>
                          <small>{{ t(key(phase.saleType === 'GROUP' ? 'perBox' : 'perChair')) }}</small>
                          <small v-if="group.rowPeriods && phase.period">{{ phase.period }}</small>
                          <small v-if="phase.capacityBased">{{ t(key('capacityBased')) }}</small>
                        </div>
                        <span class="purchase-event-info__value">
                          <span class="sr-only">{{ t(key('price')) }}: </span>
                          {{ money(phase.currency, phase.ticketAmount) }}
                        </span>
                      </li>
                    </ul>
                  </section>
                </div>
                <p v-if="!phaseGroups.length">{{ t(key('noPhases')) }}</p>
              </section>
              <section v-if="information.localities.length">
                <h3>{{ t(key('capacityTitle')) }}</h3>
                <p class="purchase-event-info__hint">{{ t(key('capacityLegend')) }}</p>
                <div class="purchase-event-info__group">
                  <ul role="list" :aria-label="t(key('capacityTitle'))">
                    <li v-for="locality in information.localities" :key="locality.name">
                      <span class="purchase-event-info__name">{{ locality.name }}</span>
                      <span class="purchase-event-info__value" aria-hidden="true">
                        {{ locality.available == null ? '—' : count(locality.available) }} / {{ count(locality.capacity) }}
                      </span>
                      <span class="sr-only">
                        {{ t(key('availability')) }}: {{ locality.available ?? '—' }}.
                        {{ t(key('totalCapacity')) }}: {{ locality.capacity }}
                      </span>
                    </li>
                  </ul>
                </div>
              </section>
            </div>
          </template>
          <template #gallery>
            <section
              v-if="gallery.length"
              class="purchase-event-info__gallery"
              :aria-label="t(key('gallery'))"
              aria-roledescription="carousel"
            >
              <h3>{{ t(key('gallery')) }}</h3>
              <div class="purchase-event-info__gallery-viewport">
                <Transition name="purchase-gallery" mode="out-in">
                  <div
                    v-if="currentGalleryAsset"
                    :key="currentGalleryAsset.id"
                    class="purchase-event-info__slide"
                  >
                    <PublicEventMediaPlayer
                      v-if="currentGalleryAsset.mediaType.startsWith('video/')"
                      kind="video"
                      natural-size
                      :src="currentGalleryAsset.contentPath"
                      :label="`${event.name} · ${slide + 1}`"
                    />
                    <PublicPicture
                      v-else
                      :image="currentGalleryAsset"
                      sizes="(min-width: 768px) 70vw, 100vw"
                      :alt="`${event.name} · ${slide + 1}`"
                      loading="lazy"
                    />
                  </div>
                </Transition>
              </div>
              <div class="purchase-event-info__gallery-actions">
                <TqButton
                  compact
                  variant="secondary"
                  icon="i-lucide-chevron-left"
                  :icon-only-label="t(key('previous'))"
                  :disabled="slide === 0"
                  @click="slide--"
                />
                <span aria-live="polite"
                  >{{ slide + 1 }} / {{ gallery.length }}</span
                >
                <TqButton
                  compact
                  variant="secondary"
                  icon="i-lucide-chevron-right"
                  :icon-only-label="t(key('next'))"
                  :disabled="slide === gallery.length - 1"
                  @click="slide++"
                />
              </div>
            </section>
          </template>
          <template #location>
            <section
              v-if="venueLocation && directionsUrl"
              class="purchase-event-info__location"
              :aria-label="t(key('locationMap'))"
            >
              <ClientOnly>
                <LocationMap
                  :location="venueLocation"
                  :label="t(key('locationMap'))"
                  :error-label="t(key('locationMapError'))"
                />
                <template #fallback
                  ><p role="status">
                    {{ $t('accessibility.loading') }}
                  </p></template
                >
              </ClientOnly>
              <div class="purchase-event-info__place">
                <span class="purchase-event-info__fact-icon"
                  ><UIcon name="i-lucide-map-pin" aria-hidden="true"
                /></span>
                <div>
                  <strong>{{
                    event.venue?.name ?? t(key('locationMap'))
                  }}</strong>
                  <span
                    v-if="event.venue?.address"
                    class="purchase-event-info__address"
                    >{{ event.venue.address }}</span
                  >
                </div>
                <TqButton
                  :to="directionsUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary"
                  icon="i-lucide-route"
                >
                  {{ t(key('directions')) }}
                </TqButton>
              </div>
            </section>
          </template>
        </UTabs>
      </div>
    </template>
  </UModal>
  <UModal
    v-model:open="guideOpen"
    :title="t(key('guideTitle'))"
    :description="t(key('guideIntro'))"
    :ui="{
      content:
        'left-0 top-0 h-dvh w-dvw max-h-none max-w-none translate-x-0 translate-y-0 rounded-none shadow-none ring-0 sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-[calc(100%-2rem)] sm:max-h-[90dvh] sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg sm:shadow-lg sm:ring',
      header: 'pe-14 sm:pe-16',
      title: 'text-lg leading-snug',
      close: 'top-3 end-3 sm:top-4 sm:end-4',
      body: 'min-h-0 overflow-y-auto',
      footer: 'justify-end',
    }"
  >
    <template #body>
      <UAccordion
        :items="guideItems"
        default-value="choose"
        class="purchase-guide"
        :ui="{ trigger: 'gap-3 py-3.5 text-base', body: 'pb-4 ps-11' }"
      >
        <template #leading="{ item }">
          <span class="purchase-guide__marker" :class="{ 'purchase-guide__marker--help': !item.step }" aria-hidden="true">
            <template v-if="item.step">{{ item.step }}</template>
            <UIcon v-else name="i-lucide-life-buoy" />
          </span>
        </template>
      </UAccordion>
    </template>
    <template #footer>
      <TqButton variant="secondary" icon="i-lucide-route" @click="guideOpen = false; emit('tour')">
        {{ t(key('guideTourCta')) }}
      </TqButton>
    </template>
  </UModal>
</template>

<style scoped>
.purchase-help-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.75rem;
  min-width: 0;
  max-width: 100%;
}
.purchase-help-actions :deep(.tq-button__content) {
  white-space: normal;
}
.purchase-help-actions :deep(.tq-button__content > .iconify) {
  flex-shrink: 0;
}
.purchase-help-actions__details {
  position: relative;
  isolation: isolate;
  box-shadow: var(--tq-shadow-soft);
}
.purchase-help-actions__details::after {
  position: absolute;
  inset: 0;
  border: 2px solid var(--tq-on-action);
  border-radius: inherit;
  content: '';
  opacity: 0;
  pointer-events: none;
  animation: purchase-information-ring 3s var(--tq-ease-soft) infinite;
}
.purchase-help-actions__arrow {
  flex-shrink: 0;
  transition: transform 0.2s var(--tq-ease-soft);
}
.purchase-help-actions__details:hover .purchase-help-actions__arrow,
.purchase-help-actions__details:focus-visible .purchase-help-actions__arrow {
  transform: translate(0.125rem, -0.125rem);
}
@keyframes purchase-information-ring {
  0%,
  20%,
  100% {
    opacity: 0;
    box-shadow: none;
  }
  45%,
  60% {
    opacity: 0.9;
    box-shadow:
      0 0 0.25rem 0.1rem color-mix(in oklch, var(--tq-action) 80%, transparent),
      0 0 0.65rem 0.25rem color-mix(in oklch, var(--tq-action) 45%, transparent),
      0 0 1.15rem 0.5rem color-mix(in oklch, var(--tq-action) 20%, transparent),
      0 0 1.8rem 0.75rem color-mix(in oklch, var(--tq-action) 8%, transparent);
  }
  85% {
    opacity: 0;
    box-shadow:
      0 0 0.5rem 0.25rem color-mix(in oklch, var(--tq-action) 35%, transparent),
      0 0 1rem 0.5rem color-mix(in oklch, var(--tq-action) 20%, transparent),
      0 0 1.5rem 0.75rem color-mix(in oklch, var(--tq-action) 8%, transparent),
      0 0 2rem 1rem color-mix(in oklch, var(--tq-action) 2%, transparent);
  }
}
/* One slim row on phones so the map, not the help, owns the first screen. */
@media (max-width: 640px) {
  .purchase-help-actions {
    flex-wrap: nowrap;
    gap: 0.5rem;
    width: 100%;
    overflow-x: auto;
    padding: 0.25rem 0.25rem 0.5rem;
    margin: -0.25rem -0.25rem -0.5rem;
    scrollbar-width: none;
  }
  .purchase-help-actions > * {
    flex: none;
    min-height: 2.5rem;
    padding-block: 0.35rem;
    padding-inline: 0.8rem;
    font-size: 0.85rem;
  }
  .purchase-help-actions :deep(.tq-button__content) {
    white-space: nowrap;
  }
}
@media (prefers-reduced-motion: reduce) {
  .purchase-help-actions__details::after {
    animation: none;
  }
  .purchase-help-actions__arrow {
    transition: none;
  }
}
.purchase-event-info__overview {
  display: grid;
  gap: 1.5rem;
  min-width: 0;
}
.purchase-event-info__overview > * {
  min-width: 0;
}
.purchase-event-info__about {
  display: grid;
  align-content: start;
  gap: 1.5rem;
}
.purchase-event-info__hero {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;
}
.purchase-event-info__poster {
  position: relative;
  min-width: 0;
}
.purchase-event-info__banner {
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: min(50dvh, 26rem);
  margin-inline: auto;
  object-fit: contain;
  border-radius: var(--tq-radius-lg);
  box-shadow: var(--tq-shadow-tight);
}
.purchase-event-info__meta {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.5rem;
}
.purchase-event-info__services {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0;
  margin: 0;
  list-style: none;
}
.purchase-event-info__services li {
  display: flex;
  min-width: 0;
  max-width: 100%;
}
/* The map is the stage; the venue floats over it as a card, like native map apps. */
.purchase-event-info__location {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-muted);
}
.purchase-event-info__place {
  position: absolute;
  z-index: 1;
  inset-inline: 0.75rem;
  /* Leaves Leaflet's OpenStreetMap attribution readable below the card. */
  bottom: 1.75rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-elevated);
  box-shadow: var(--tq-shadow-soft);
  line-height: 1.4;
}
.purchase-event-info__place > div {
  display: grid;
  gap: 0.125rem;
  min-width: 0;
}
.purchase-event-info__place strong {
  color: var(--tq-text-heading);
  font-size: 1.05rem;
}
.purchase-event-info__place .purchase-event-info__address {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.purchase-event-info__place > :last-child {
  grid-column: 1 / -1;
  justify-content: center;
}
.purchase-event-info__location :deep(.location-map) {
  isolation: isolate;
  max-width: 100%;
  border: 0;
  border-radius: 0;
}
.purchase-event-info__location :deep(.location-map__canvas) {
  height: var(--purchase-location-height);
  aspect-ratio: auto;
}
@media (min-width: 640px) {
  .purchase-event-info__place {
    right: auto;
    width: min(26rem, calc(100% - 1.5rem));
  }
}
.purchase-event-info {
  --purchase-event-gutter: 1rem;
  /* Fills the full-screen sheet below its header and tabs. */
  --purchase-location-height: clamp(20rem, calc(100dvh - 12rem), 44rem);
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
  gap: 1.5rem;
  padding: 0 var(--purchase-event-gutter) var(--purchase-event-gutter);
  overflow-wrap: anywhere;
}
@media (min-width: 640px) {
  .purchase-event-info {
    --purchase-event-gutter: 1.5rem;
  }
}
.purchase-event-info > *,
.purchase-event-info__hero > * {
  min-width: 0;
  max-width: 100%;
}
.purchase-event-info h3 {
  margin: 0 0 0.5rem;
}
.purchase-event-info p {
  color: var(--tq-text-soft);
  line-height: 1.65;
}
.purchase-event-info__overview h3,
.purchase-event-info__gallery h3 {
  color: var(--tq-text-muted);
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.purchase-event-info__facts {
  display: grid;
  gap: 0.875rem;
  padding: 1rem;
  margin: 0;
  list-style: none;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-elevated);
}
.purchase-event-info__facts li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  line-height: 1.4;
}
.purchase-event-info__fact-icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--tq-radius-md);
  background: var(--tq-accent-chip-bg);
  color: var(--tq-accent-readable);
  font-size: 1.125rem;
}
.purchase-event-info__facts li > div {
  display: grid;
  gap: 0.125rem;
  min-width: 0;
}
.purchase-event-info__facts strong {
  color: var(--tq-text-heading);
}
.purchase-event-info__address,
.purchase-event-info__legend,
.purchase-event-info__name small,
.purchase-event-info__group header span {
  color: var(--tq-text-muted);
  font-size: 0.8125rem;
  line-height: 1.4;
}
.purchase-event-info__legend {
  margin: -0.25rem 0 0.5rem;
}
.purchase-event-info__groups {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 0.75rem;
}
.purchase-event-info__group {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-md);
  background: var(--tq-bg-elevated);
}
.purchase-event-info__group header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.125rem 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--tq-border);
  background: var(--tq-bg-muted);
}
.purchase-event-info__group h4 {
  margin: 0;
  color: var(--tq-text-heading);
}
.purchase-event-info__group ul {
  padding: 0;
  margin: 0;
  list-style: none;
}
.purchase-event-info__group li {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  color: var(--tq-text);
}
.purchase-event-info__group li + li {
  border-top: 1px solid var(--tq-border);
}
.purchase-event-info__name {
  display: grid;
  gap: 0.125rem;
  min-width: 0;
  font-weight: 600;
}
.purchase-event-info__name small {
  font-weight: 400;
}
.purchase-event-info__value {
  flex: none;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: end;
  white-space: nowrap;
  color: var(--tq-text-heading);
}
.purchase-event-info__tabs :deep([data-slot='list']) {
  position: sticky;
  top: 0;
  z-index: 1;
  width: calc(100% + var(--purchase-event-gutter) * 2);
  margin-inline: calc(var(--purchase-event-gutter) * -1);
  padding: 0.5rem var(--purchase-event-gutter);
  border-bottom: 1px solid var(--tq-border);
  background: var(--tq-bg-elevated);
  scrollbar-width: none;
}
.purchase-event-info__tabs :deep([data-slot='trigger']) {
  min-height: 2.5rem;
  border: 1px solid transparent;
  border-radius: var(--tq-radius-pill);
  color: var(--tq-text-muted);
  font-weight: 600;
}
.purchase-event-info__tabs :deep([data-slot='trigger']::after) {
  display: none;
}
.purchase-event-info__tabs :deep([data-slot='trigger']:hover) {
  background: var(--tq-bg-muted);
  color: var(--tq-text);
}
.purchase-event-info__tabs :deep([data-slot='trigger'][data-state='active']) {
  border-color: color-mix(in oklch, var(--tq-accent-readable) 35%, var(--tq-border));
  background: var(--tq-accent-chip-bg);
  color: var(--tq-accent-readable);
}
.purchase-event-info__rates {
  display: grid;
  gap: 1.5rem;
  min-width: 0;
}
.purchase-event-info__rates h3 {
  margin: 0;
  color: var(--tq-text-muted);
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.purchase-event-info__hint {
  margin: 0.35rem 0 0;
  color: var(--tq-text-muted);
  font-size: 0.875rem;
  line-height: 1.45;
}
.purchase-event-info__rates .purchase-event-info__group {
  margin-top: 0.75rem;
}
.purchase-event-info__groups .purchase-event-info__group {
  margin-top: 0;
}
.purchase-event-info__fact-label {
  color: var(--tq-text-muted);
  font-size: 0.75rem;
  line-height: 1.3;
}
.purchase-event-info__video {
  width: fit-content;
  max-width: min(100%, calc(48rem * var(--purchase-video-scale, 1)));
  margin-inline: auto;
}
.purchase-event-info :deep(.event-media-player--video) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, auto));
  justify-content: center;
  justify-items: center;
  gap: 0.75rem;
}
.purchase-event-info :deep(.event-media-player--video > video),
.purchase-event-info :deep(.event-media-player--video > p) {
  grid-column: 1 / -1;
}
.purchase-event-info__video :deep(video) {
  width: auto;
  max-width: min(100%, calc(80% * var(--purchase-video-scale, 1)));
  height: auto;
  max-height: min(
    calc(46dvh * var(--purchase-video-scale, 1)),
    calc(30rem * var(--purchase-video-scale, 1))
  );
  border-radius: var(--tq-radius-md);
}
.purchase-event-info .purchase-event-info__video {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.purchase-event-info__video :deep(.tq-button) {
  justify-self: start;
}
.purchase-event-info__video :deep(video + .tq-button) {
  justify-self: end;
}
.purchase-event-info__video :deep(video) {
  justify-self: center;
}
.purchase-event-info__video :deep(.tq-button__content),
.purchase-event-info__video-sizing :deep(.tq-button__content) {
  white-space: nowrap;
}
.purchase-event-info__video-sizing {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
.purchase-event-info__video-sizing [role='status'] {
  min-width: 3rem;
  text-align: center;
  color: var(--tq-text-soft);
  font-variant-numeric: tabular-nums;
}
.purchase-event-info__gallery-viewport {
  width: 100%;
  height: clamp(16rem, 52dvh, 34rem);
  overflow: hidden;
  border-radius: var(--tq-radius-md);
  background: var(--tq-bg-muted);
}
.purchase-event-info__slide {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}
.purchase-event-info__slide img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.purchase-event-info__slide :deep(.event-media-player--video) {
  width: 100%;
  height: 100%;
  min-height: 0;
  grid-template-rows: minmax(0, 1fr) auto;
  padding: 0.75rem;
}
.purchase-event-info__slide :deep(video) {
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  object-fit: contain;
}
.purchase-gallery-enter-active,
.purchase-gallery-leave-active {
  transition: opacity 180ms var(--tq-ease-soft);
}
.purchase-gallery-enter-from,
.purchase-gallery-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .purchase-gallery-enter-active,
  .purchase-gallery-leave-active {
    transition: none;
  }
}
.purchase-event-info__gallery-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
}
.purchase-event-info__floor-plan {
  display: block;
  width: 100%;
  height: auto;
  max-height: 70dvh;
  object-fit: contain;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-lg);
  background: var(--tq-bg-elevated);
}
@media (max-width: 767px) {
  .purchase-event-info__control-label,
  .purchase-event-info :deep(.event-media-player__label) {
    display: none;
  }
}
/* From 560px the poster becomes its own column; facts, services and plan stack beside it. */
@media (min-width: 560px) {
  .purchase-event-info__about:has(.purchase-event-info__poster) {
    grid-template-columns: clamp(11rem, 32%, 17rem) minmax(0, 1fr);
    column-gap: 1.5rem;
  }
  .purchase-event-info__about:has(.purchase-event-info__poster) .purchase-event-info__hero {
    display: contents;
  }
  .purchase-event-info__about:has(.purchase-event-info__poster) section {
    grid-column: 2;
  }
  .purchase-event-info__poster {
    grid-column: 1;
    grid-row: 1 / span 3;
  }
  .purchase-event-info__banner {
    width: 100%;
    max-height: none;
  }
}
@media (min-width: 640px) {
  .purchase-event-info {
    --purchase-location-height: clamp(18rem, calc(90dvh - 13.5rem), 40rem);
  }
}
@media (min-width: 900px) {
  .purchase-event-info__about:not(:has(.purchase-event-info__poster)),
  .purchase-event-info__rates:has(> section + section) {
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    align-items: start;
  }
}
.purchase-guide :deep([data-slot='item']) {
  border-bottom: 1px solid var(--tq-border);
}
.purchase-guide :deep([data-slot='label']) {
  color: var(--tq-text-heading);
  font-weight: 600;
}
.purchase-guide :deep([data-slot='body']) {
  color: var(--tq-text-soft);
  line-height: 1.65;
}
.purchase-guide__marker {
  display: grid;
  flex: none;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: var(--tq-radius-pill);
  background: var(--tq-accent-chip-bg);
  color: var(--tq-accent-readable);
  font-size: 0.875rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.purchase-guide__marker--help {
  background: var(--tq-bg-muted);
  color: var(--tq-text-muted);
}
</style>
